/**
 * "Do not start autoplay until levels of detail are fully loaded" — the scene option
 * `autoPlayWaitForFullDetail`.
 *
 * The preloader still releases the scene at its usual threshold (waitForLodReveal in
 * createViewer.ts). This gate only holds the camera movement of the scene's own initial
 * autoplay after that point, while a centred "Adding in more detail" status is shown. After
 * BUTTON_DELAY_MS of that status a "Start the tour anyways" button appears.
 *
 * What "fully loaded" means — one definition, shared with the post-reveal detail monitor and
 * the on-demand render scheduler's `lod-streaming` source:
 *   the engine's pending LOD-node load count (summed over the unified gsplat managers; the same
 *   number the unified `frame:ready` event reports as `loadingCount`) has stayed at 0 for
 *   SETTLE_MS without interruption.
 * That is "everything this device's LOD budget wants for the current view has arrived", not
 * "every octree node": mobile budgets never load the whole octree. When the pending count is
 * unreadable the detail monitor's fallback applies — the active splat count must stop growing
 * for FALLBACK_FLAT_MS. When neither signal is readable, detail counts as complete.
 *
 * Which autoplay waits (see shouldWaitForDetailBeforeAutoplay): only the initial autoplay of a
 * scene whose own Auto Play is on, in the published viewer, and only for LOD-streaming scenes.
 * A single PLY/SOG/compressed file is fully loaded when the preloader releases it, so it starts
 * exactly as before. An explicit `viewer.play()` (or the play button) always starts immediately
 * and dismisses the status.
 *
 * Visitor control while waiting follows the existing autoplay rules: whatever pauses a playing
 * tour (next/prev, camera-mode switch incl. movement keys, FPV, staging photos, camera hold, frame
 * capture, pause()/stop()) cancels the pending start — the status goes away and the tour never
 * starts on its own. Scroll-wheel scrubbing and drag-to-look do not pause a playing tour, so they
 * do not cancel the wait either. Destroy and portal navigation cancel it via pause().
 *
 * NEVER hangs: after MAX_WAIT_MS the tour starts anyway (unattended screens, stalled streams).
 */

export interface AutoplayDetailGateTiming {
    /** Pending count must stay at 0 this long before detail counts as settled (ms). */
    settleMs: number;
    /** Only show the status once the wait has lasted this long and detail is still loading (ms). */
    showDelayMs: number;
    /** Reveal "Start the tour anyways" this long after the status appears (ms). */
    buttonDelayMs: number;
    /** Start the tour anyway after waiting this long (ms). */
    maxWaitMs: number;
    /** How often the detail signals are sampled (ms). */
    pollMs: number;
    /** Fallback signal only: splat count flat this long = "not loading" (ms). */
    fallbackFlatMs: number;
}

export const AUTOPLAY_DETAIL_TIMING: Readonly<AutoplayDetailGateTiming> = Object.freeze({
    settleMs: 800,          // same anti-flicker hold as the detail monitor's HIDE_DEBOUNCE_MS
    showDelayMs: 300,       // no flash when the last chunk lands right after the reveal
    buttonDelayMs: 5000,    // "after 5 seconds of that, there's a button"
    maxWaitMs: 60000,       // matches the detail monitor's 60 s safety stop
    pollMs: 100,
    fallbackFlatMs: 5000,   // same as the detail monitor's FALLBACK_FLAT_MS
});

export interface DetailSignals {
    /** Pending LOD-node downloads summed across the gsplat managers; -1 when unreadable. */
    readPending(): number;
    /** Active splat count; -1 when unreadable. Only consulted when pending is unreadable. */
    readCount(): number;
}

/**
 * Returns a sampler answering "is detail still streaming at time `t`?" using the same rules as
 * the detail monitor: pending > 0 when readable, otherwise "the splat count grew recently".
 */
export function createDetailLoadingSampler(
    signals: DetailSignals,
    startedAt: number,
    fallbackFlatMs: number = AUTOPLAY_DETAIL_TIMING.fallbackFlatMs,
): (t: number) => boolean {
    let lastCount = -1;
    let lastGrowthAt = startedAt;
    return (t: number): boolean => {
        const pending = signals.readPending();
        if (pending >= 0) return pending > 0;
        const count = signals.readCount();
        if (count < 0) return false;
        if (count > lastCount) {
            lastCount = count;
            lastGrowthAt = t;
        }
        return t - lastGrowthAt < fallbackFlatMs;
    };
}

export interface AutoplayDetailWaitDecision {
    /** The scene's own Auto Play setting (`autoPlayEnabled` → config.autoPlay). The option is a
     *  sub-setting of it: with the scene's Auto Play off (e.g. `?autoplay=true` on such a scene),
     *  autoplay starts as before. */
    sceneAutoPlay: boolean | undefined;
    /** The scene's `autoPlayWaitForFullDetail` value. */
    optionEnabled: boolean | undefined;
    /** The scene streams levels of detail (lod-meta.json). */
    isLodScene: boolean;
    /** Editor sessions preview autoplay without the wait. */
    isEditor: boolean;
    /** 4DGS frame sequences have no LOD stream to wait for. */
    isFrameSequence: boolean;
}

/** Whether the scene's own initial autoplay should wait for detail. False = start as before. */
export function shouldWaitForDetailBeforeAutoplay(decision: AutoplayDetailWaitDecision): boolean {
    return decision.sceneAutoPlay === true
        && decision.optionEnabled === true
        && decision.isLodScene
        && !decision.isEditor
        && !decision.isFrameSequence;
}

export interface AutoplayDetailGateUI {
    /** Show the centred "Adding in more detail" status. */
    show(): void;
    /** Reveal the "Start the tour anyways" button inside the status. */
    showStartButton(): void;
    /** Remove the status. Called exactly once per shown status. */
    hide(): void;
}

/** settled: detail finished; start-anyway: visitor pressed the button; max-wait: safety cap. */
export type AutoplayDetailGateOutcome = 'settled' | 'start-anyway' | 'max-wait' | 'cancelled';

export interface AutoplayDetailGateDeps extends DetailSignals {
    /** Starts the tour exactly as autoplay does without the option. */
    startPlayback(): void;
    ui: AutoplayDetailGateUI;
    now?: () => number;
    /** Timer injection for tests; defaults to window.setTimeout / window.clearTimeout. */
    setTimer?: (callback: () => void, ms: number) => number;
    clearTimer?: (handle: number) => void;
    timing?: Partial<AutoplayDetailGateTiming>;
    onEnd?: (outcome: AutoplayDetailGateOutcome, waitedMs: number) => void;
}

export type AutoplayDetailGatePhase = 'idle' | 'armed' | 'waiting' | 'done';

export interface AutoplayDetailGate {
    /** Initial autoplay was requested; the wait begins once the preloader has released the scene. */
    arm(): void;
    /** Whether an armed request is waiting for the preloader. */
    isArmed(): boolean;
    /** Start waiting for detail (consumes the armed request). */
    begin(): void;
    /** The visitor pressed "Start the tour anyways": start the tour now. */
    startNow(): void;
    /** The visitor took control, playback was paused/stopped, or the viewer is going away. */
    cancel(): void;
    /** Armed or waiting — the initial autoplay is still pending behind this gate. */
    isEngaged(): boolean;
    /** Whether the centred status is currently on screen. */
    isStatusVisible(): boolean;
    phase(): AutoplayDetailGatePhase;
}

export function createAutoplayDetailGate(deps: AutoplayDetailGateDeps): AutoplayDetailGate {
    const timing: AutoplayDetailGateTiming = { ...AUTOPLAY_DETAIL_TIMING, ...deps.timing };
    const now = deps.now ?? (() => performance.now());
    const setTimer = deps.setTimer ?? ((callback: () => void, ms: number) => window.setTimeout(callback, ms));
    const clearTimer = deps.clearTimer ?? ((handle: number) => window.clearTimeout(handle));

    let phase: AutoplayDetailGatePhase = 'idle';
    let startedAt = 0;
    let notLoadingSince = -1;
    let statusVisible = false;
    let pollTimer: number | null = null;
    let buttonTimer: number | null = null;
    let isLoading: ((t: number) => boolean) | null = null;

    const clearTimers = () => {
        if (pollTimer !== null) { clearTimer(pollTimer); pollTimer = null; }
        if (buttonTimer !== null) { clearTimer(buttonTimer); buttonTimer = null; }
    };

    const hideStatus = () => {
        if (!statusVisible) return;
        statusVisible = false;
        deps.ui.hide();
    };

    const finish = (outcome: AutoplayDetailGateOutcome) => {
        const wasWaiting = phase === 'waiting';
        phase = 'done';
        clearTimers();
        hideStatus();
        isLoading = null;
        if (outcome !== 'cancelled') deps.startPlayback();
        if (wasWaiting) deps.onEnd?.(outcome, now() - startedAt);
    };

    const showStatus = () => {
        statusVisible = true;
        deps.ui.show();
        buttonTimer = setTimer(() => {
            buttonTimer = null;
            if (phase === 'waiting' && statusVisible) deps.ui.showStartButton();
        }, timing.buttonDelayMs);
    };

    const poll = () => {
        pollTimer = null;
        if (phase !== 'waiting' || !isLoading) return;
        const t = now();
        const elapsed = t - startedAt;
        if (isLoading(t)) {
            notLoadingSince = -1;
            if (!statusVisible && elapsed >= timing.showDelayMs) showStatus();
        } else {
            if (notLoadingSince < 0) notLoadingSince = t;
            if (t - notLoadingSince >= timing.settleMs) {
                finish('settled');
                return;
            }
        }
        if (elapsed >= timing.maxWaitMs) {
            finish('max-wait');
            return;
        }
        pollTimer = setTimer(poll, timing.pollMs);
    };

    return {
        arm(): void {
            if (phase === 'waiting') return;
            phase = 'armed';
        },
        isArmed(): boolean {
            return phase === 'armed';
        },
        begin(): void {
            if (phase === 'waiting') return;
            phase = 'waiting';
            startedAt = now();
            notLoadingSince = -1;
            isLoading = createDetailLoadingSampler(deps, startedAt, timing.fallbackFlatMs);
            poll();
        },
        startNow(): void {
            if (phase !== 'waiting') return;
            finish('start-anyway');
        },
        cancel(): void {
            if (phase === 'armed') {
                phase = 'idle';
                return;
            }
            if (phase !== 'waiting') return;
            finish('cancelled');
        },
        isEngaged(): boolean {
            return phase === 'armed' || phase === 'waiting';
        },
        isStatusVisible(): boolean {
            return statusVisible;
        },
        phase(): AutoplayDetailGatePhase {
            return phase;
        },
    };
}
